import type { ApolloCache, ApolloClient } from "@apollo/client";

import type {
  GetIssueQuery,
  GetIssueQueryVariables,
  MarkIssueAsSeenMutation,
} from "@medbillai/graphql-types";

import type { PillProps, SelectBarTabs } from "@medbillai/ui";

import { exhaustiveGuard } from "@medbillai/utils";

import { getUnreadIssuesCountQuery } from "../../../provider/issue-events/IssueEventsProvider";
import { getIssueQuery } from "../../case/utils/queries";
import type { BaseEvent, CaseScreenIssue } from "../../case/utils/types";
import { getIssuesQueryV2 } from "./queries";
import type { Issue, IssueStatus, IssueV2, QueryIssuesV2 } from "./types";

export const pageSize = 15;

export const openFilter = {
  open: true,
  closed: false,
  unread: false,
  first: pageSize,
  after: 0,
};

export const unreadFilter = {
  closed: false,
  unread: true,
  open: false,
  first: pageSize,
  after: 0,
};

export const closedFilter = {
  unread: false,
  closed: true,
  open: false,
  first: pageSize,
  after: 0,
};

export const filterOptionIds = {
  open: "open",
  unread: "unread",
  read: "read",
  closed: "closed",
};

export const filterOptions: SelectBarTabs = [
  { id: filterOptionIds.open, name: "Open" },
  { id: filterOptionIds.unread, name: "Unread" },
  { id: filterOptionIds.closed, name: "Closed" },
];

export const filterMap: Record<string, typeof openFilter> = {
  [filterOptionIds.open]: openFilter,
  [filterOptionIds.unread]: unreadFilter,
  [filterOptionIds.closed]: closedFilter,
};

export const formatEvent = (event: Issue["lastEvent"]) => {
  if (!event) {
    return "";
  }
  switch (event.__typename) {
    case "issue__ChatEvent_Type":
      return event.chatBody;
    case "issue__ClosedEvent_Type":
      return "Issue closed";
    case "issue__DocumentUploadedEvent_Type":
      return "Document uploaded";
    case "issue__ReopenedEvent_Type":
      return "Issue reopened";
    case "issue__HipaaReleaseRequestedEvent_Type":
      return "HIPAA release requested";
    case "issue__TemplateReleaseRequestedEvent_Type":
      return `${event.template.description} requested`;
    case "issue__BillDisputeEvent_Type":
      return "Bill disputed";
    case "issue__PaymentAgreementRequestedEvent_Type":
      return "We found potential savings!";
    case "issue__ClosurePaymentRequestedEvent_Type":
      return "Payment Accepted";
    case "issue__BenefitsSummaryGeneratedEvent_Type":
      return "Benefits summary generated";
    case "issue__PlanSelectionEvent_Type":
      return "Select plan";
    case "issue__UserSelectionRequestedEvent_Type":
      return "Here are some options you can choose from:";
    case "issue__ConnectInsuranceRequestedEvent_Type":
      return (
        event.insuranceLinkMessage ??
        "To start getting personalized help, connect your insurance."
      );
    case "issue__ConnectAccountRequestedEvent_Type":
      return "To start getting personalized help, connect your account.";
    case "issue__BillPaymentRequestedEvent_Type":
      return "Bill payment requested";
    case "issue__BillPaymentSuccessEvent_Type":
      return "Payment completed";
    case "issue__DocumentAnalysisEvent_Type":
      return "Document analysis";
    // These events are not visible to the user, so they shouldn't be
    // returned in userVisibleEvents and thus we don't need to format them.
    case "issue__CommentEvent_Type":
    case "issue__InternalDocumentUploadedEvent_Type":
      return "";
    default:
      exhaustiveGuard(event);
  }
};

export const getTextColor = (status: IssueStatus) => {
  return status === "NEW_ACTIVITY" ? "$color" : "$textSecondary";
};

export const getTimeColor = (status: IssueStatus) => {
  return status === "NEW_ACTIVITY" ? "$color" : "$textSecondary";
};

export const cleanMarkdownText = (text: string | null) => {
  if (!text) {
    return "";
  }
  return text.replace(/[\\^$*+?.()|[\]{}#]/g, "");
};

/**
 * Function to convert `openNewIssue` to `issue__Issue, Technically this could
 * also be a full getIssueQueryEvent but it will quickly get over written so for
 * type saftery we will convert it.
 */
export function convertToIssue(openNewIssue: CaseScreenIssue): IssueV2 {
  return {
    __typename: "issue__Issue_Type",
    id: openNewIssue.id,
    title: openNewIssue.title,
    openedAt: openNewIssue.openedAt,
    closedAt: openNewIssue.closedAt,
    lastEvent: openNewIssue.lastEvent,
    firstUnseenEvent: openNewIssue.firstUnseenEvent,
  };
}

/**
 * Optimistically updates the cache so that the issues list are synced
 */
export function updateIssuesQuery(
  cache: ApolloCache<unknown>,
  openNewIssue: CaseScreenIssue,
) {
  cache.updateQuery(
    {
      query: getIssuesQueryV2,
    },
    queryData => {
      if (!queryData?.queryIssuesV2) {
        return null;
      }

      const newIssue = convertToIssue(openNewIssue);

      const updatedEvents: QueryIssuesV2 = [
        newIssue,
        ...queryData.queryIssuesV2,
      ];

      return {
        ...queryData,
        queryIssuesV2: updatedEvents,
      };
    },
  );
}

/**
 * Optimistically updates the cache so that the notification statuses are synced.
 */
export function updateIssueAsSeenInCache(
  cache: ApolloCache<unknown>,
  issueId: string,
) {
  const cachedData = cache.readQuery({
    query: getIssuesQueryV2,
  });

  if (!cachedData?.queryIssuesV2) {
    return;
  }

  const updatedIssues = cachedData.queryIssuesV2.map(issue => {
    if (issue.id === issueId) {
      return {
        ...issue,
        firstUnseenEvent: null,
      };
    }
    return issue;
  });

  cache.writeQuery({
    query: getIssuesQueryV2,
    data: {
      ...cachedData,
      queryIssuesV2: updatedIssues,
    },
  });

  updateUnreadIssuesCount(cache, issueId);
}

function updateUnreadIssuesCount(cache: ApolloCache<unknown>, issueId: string) {
  const cachedUnreadIssuesCount = cache.readQuery({
    query: getUnreadIssuesCountQuery,
  });

  if (!cachedUnreadIssuesCount?.me) {
    return;
  }

  const updatedIssues =
    cachedUnreadIssuesCount.me.issues?.filter(issue => issue.id !== issueId) ||
    [];

  cache.writeQuery({
    query: getUnreadIssuesCountQuery,
    data: {
      ...cachedUnreadIssuesCount,
      me: {
        ...cachedUnreadIssuesCount.me,
        issues: updatedIssues,
      },
    },
  });
}

export function handleUpdateForSeenIssue(
  cache: ApolloCache<unknown>,
  data: MarkIssueAsSeenMutation | null | undefined,
) {
  if (!data) {
    return;
  }
  const issueId = data.updateIssue?.id;
  if (issueId) {
    updateIssueAsSeenInCache(cache, issueId);
  }
}

export function getIssueStatus({
  closedAt,
  firstUnseenEventId,
}: {
  closedAt?: string | null;
  firstUnseenEventId?: string | null;
}): "CLOSED" | "NEW_ACTIVITY" | "PROCESSING" {
  return firstUnseenEventId
    ? "NEW_ACTIVITY"
    : closedAt
      ? "CLOSED"
      : "PROCESSING";
}

export const getChatPillStyle = (status: IssueStatus): PillProps => {
  switch (status) {
    case "CLOSED":
      return { text: "Closed", notificationDot: false, theme: "gray" };
    case "NEW_ACTIVITY":
      return {
        text: "New",
        notificationDot: true,
        theme: "green",
      };
    default:
      return {
        text: "Open",
        notificationDot: false,
        theme: "lightblue",
      };
  }
};

export const addEventToGetIssueQueryCache = ({
  client,
  variables,
  newEvent,
}: {
  client: ApolloClient<object>;
  variables: GetIssueQueryVariables;
  newEvent: BaseEvent;
}) => {
  const existingData = client.readQuery<GetIssueQuery>({
    query: getIssueQuery,
    variables,
  });
  const existingIssue = existingData?.issue__Issue?.[0];
  if (existingData && existingIssue) {
    const newIssueData = {
      ...existingIssue,
      userVisibleEvents: [...(existingIssue.userVisibleEvents ?? []), newEvent],
      lastEvent: newEvent,
    };
    client.writeQuery({
      query: getIssueQuery,
      variables,
      data: {
        __typename: "Query",
        issue__Issue: [newIssueData],
        me: existingData.me ?? {
          __typename: "User_Type",
          id: variables.userId,
          webAgentLinks: [],
        },
      },
    });
  }
};
